import { collection, getDocs, query, Timestamp, where, type DocumentData, type QuerySnapshot } from "firebase/firestore";
import { db } from "../firebase";
import type { PreparedImpactDisplay, ProductType } from "../lib/impact";
import type { ReportFilters } from "../lib/schema";
import { prepareImpactDisplayFromRaw } from "../lib/impact";
import { nextDayUTC, parseYMD_UTC } from "../lib/inventoryHelpers";

export async function getImpactPreviewForFilters(
  filters: ReportFilters
): Promise<{ preview: PreparedImpactDisplay }> {
  const {
    fromDate,
    toDate,
    basis,
    customerIds,
    productTypes,
  } = filters;

  const col = collection(db, "itInventory");
  const dateField: "createdAt" | "completedAt" =
    String(basis) === "createdAt" ? "createdAt" : "completedAt";

  const dateWheres: any[] = [];
  if (fromDate && fromDate.trim()) {
    const fromTs = Timestamp.fromDate(parseYMD_UTC(fromDate));
    dateWheres.push(where(dateField, ">=", fromTs));
  }
  if (toDate && toDate.trim()) {
    const toExclusive = Timestamp.fromDate(nextDayUTC(toDate));
    dateWheres.push(where(dateField, "<", toExclusive));
  }

  const mapDocToRaw = (d: any) => {
    const x = d.data() as any;
    return {
      id: d.id,
      productType: x.productTypeId ?? x.productType ?? "",
      grade: x.grade,
      reuse: !!x.reuse,
      resold: !!x.resold,
      scrap: !!x.scrap,
    };
  };

  let raws: any[] = [];

  if (productTypes && productTypes.length > 0) {
    const chunks: ProductType[][] = [];
    for (let i = 0; i < productTypes.length; i += 10) {
      chunks.push(productTypes.slice(i, i + 10));
    }

    const snaps: QuerySnapshot<DocumentData>[] = [];
    for (const chunk of chunks) {
      const ids = chunk.map((t: any) => (typeof t === "string" ? t : (t as any)?.id));
      const qBase = query(col, ...dateWheres, where("productTypeId", "in", ids));
      snaps.push(await getDocs(qBase));
    }

    raws = snaps
      .flatMap((s) => s.docs)
      .filter((d) => {
        if (!customerIds || customerIds.length === 0) return true;
        const x = d.data() as any;
        const cid = String((x as any).customerId ?? (x as any).customer ?? "");
        return (customerIds as any).includes(cid);
      })
      .map(mapDocToRaw);
  } else {
    const qBase = query(col, ...dateWheres);
    const snap = await getDocs(qBase);
    raws = snap.docs
      .filter((d) => {
        if (!customerIds || (customerIds as any).length === 0) return true;
        const x = d.data() as any;
        const cid = String((x as any).customerId ?? (x as any).customer ?? "");
        return (customerIds as any).includes(cid);
      })
      .map(mapDocToRaw);
  }

  const preview = prepareImpactDisplayFromRaw(raws as any);
  return { preview };
}
